import React from "react";
import { getImageUrl } from "utils/imageHelper";

export default function ViewProductDetails({ data }) {
  if (!data) return null;

  const resolveImage = (imgPath) => {
    if (!imgPath) return null;
    return typeof imgPath === "string" ? getImageUrl(imgPath) : null;
  };

  const defaultImageUrl = resolveImage(data.default_image);
  const galleryImages = Array.isArray(data.productImages)
    ? data.productImages.map((img) => resolveImage(img)).filter(Boolean)
    : [];

  const isActive =
    data.status?.toLowerCase() === "active" || data.status === true;

  const formatDate = (isoStr) => {
    if (!isoStr) return "N/A";
    try {
      const date = new Date(isoStr);
      return date.toLocaleString();
    } catch {
      return isoStr;
    }
  };

  return (
    <div className="flex flex-col gap-6 text-sm text-gray-800">
      {/* =====================================================
          SECTION 1: PRODUCT DETAILS
      ====================================================== */}
      <ViewSection title="Product Details">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox label="Product Name" value={data.product_name} />

          <ViewDisplayBox
            label="Exhibitor"
            value={data.exhibitor_id?.title || data.exhibitor || "N/A"}
          />

          <ViewDisplayBox
            label="Product Category"
            value={
              data.product_category_id?.title ||
              data.product_category_id?.name ||
              data.category ||
              "N/A"
            }
          />

          <ViewDisplayBox
            label="Price"
            value={
              data.price
                ? `${data.currency_id?.symbol || data.currency_id?.code || ""} ${data.price}`.trim()
                : "N/A"
            }
          />

          <ViewDisplayBox label="Website" value={data.website} />

          {/* Short Description */}
          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Short Description</span>
            <div className="view-value">
              {data.short_description || "N/A"}
            </div>
          </div>
        </div>
      </ViewSection>

      {/* =====================================================
          SECTION 2: SAMPLE DETAILS
      ====================================================== */}
      <ViewSection title="Sample Information">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="view-label">Is Sample Available</span>
            <div className="view-value flex items-center">
              {data.is_sample ? "Yes" : "No"}
            </div>
          </div>

          {data.is_sample && (
            <>
              <ViewDisplayBox
                label="Sample Start Date & Time"
                value={formatDate(data.sample_start_date)}
              />

              <ViewDisplayBox
                label="Sample End Date & Time"
                value={formatDate(data.sample_end_date)}
              />
            </>
          )}
        </div>
      </ViewSection>

      {/* =====================================================
          SECTION 3: STATUS
      ====================================================== */}
      <ViewSection title="Status">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="view-label">Status</span>
            <div className="view-value flex items-center">
              <span>{isActive ? "Active" : "Inactive"}</span>
            </div>
          </div>
        </div>
      </ViewSection>

      {/* =====================================================
          SECTION 4: PRODUCT IMAGES
      ====================================================== */}
      <ViewSection title="Product Images">
        <div className="flex flex-col gap-4">
          {/* Default Image */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Default Image</span>
            <ImageContainer
              src={defaultImageUrl}
              alt="Default Product Image"
              placeholder="No Default Image Set"
              height="h-36"
            />
          </div>

          {/* Gallery Images */}
          {galleryImages.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="view-label">Gallery Images</span>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {galleryImages.map((url, idx) => (
                  <ImageContainer
                    key={idx}
                    src={url}
                    alt={`Product Image ${idx + 1}`}
                    placeholder="No Preview"
                    height="h-28"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </ViewSection>
    </div>
  );
}

/* =========================================================
   SECTION COMPONENT
========================================================= */

function ViewSection({ title, children }) {
  return (
    <div className="view-card flex flex-col gap-6 text-sm">
      <div>
        <div className="view-subtitle">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

/* =========================================================
   DISPLAY VALUE
========================================================= */

function ViewDisplayBox({ label, value, className = "" }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="view-label">{label}</span>
      <div className="view-value">{value || "N/A"}</div>
    </div>
  );
}

/* =========================================================
   IMAGE CONTAINER
========================================================= */

function ImageContainer({
  src,
  alt,
  placeholder,
  height = "h-28",
}) {
  return (
    <div className={`${height} view-image-div`}>
      {src ? (
        <img
          src={src}
          alt={alt || "Media preview"}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="text-xs text-gray-400">{placeholder}</span>
      )}
    </div>
  );
}